import { useEffect, useId, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import type { TimelinePayload } from "@vzlive/shared";
import { t } from "@vzlive/shared/i18n";
import { Timeline } from "./Timeline";
import { orderPosts } from "../lib/timeline";

/** Pause the visible feed without disconnecting or losing incoming updates. */
export function LiveTimeline({
  timeline,
  compact = false,
}: {
  timeline: TimelinePayload;
  compact?: boolean;
}) {
  const { event, tenant, posts } = timeline;
  const locale = tenant.locale;
  const en = locale === "en";
  const [frozen, setFrozen] = useState<typeof posts | null>(null);
  const [order, setOrder] = useState(event.timelineOrder);
  const [announcement, setAnnouncement] = useState("");
  const previousIds = useRef(new Set(posts.map((post) => post.id)));
  const feedId = useId();
  const orderId = useId();
  const pending = frozen
    ? posts.filter((post) => !frozen.some((old) => old.id === post.id)).length
    : 0;

  useEffect(() => {
    const added = posts.filter(
      (post) => !previousIds.current.has(post.id),
    ).length;
    previousIds.current = new Set(posts.map((post) => post.id));
    if (!frozen && added)
      setAnnouncement(
        en
          ? `${added} new ${added === 1 ? "update" : "updates"} in the liveblog.`
          : `${added} ${added === 1 ? "neuer Beitrag" : "neue Beiträge"} im Liveblog.`,
      );
  }, [posts, frozen, en]);

  const toggleUpdates = () => {
    if (frozen) {
      setFrozen(null);
      setAnnouncement(
        en
          ? "Automatic updates resumed. The feed is up to date."
          : "Automatische Aktualisierung fortgesetzt. Der Verlauf ist auf dem neuesten Stand.",
      );
    } else {
      setFrozen(posts);
      setAnnouncement(
        en
          ? "Automatic updates paused. You can continue reading at your own pace."
          : "Automatische Aktualisierung pausiert. Du kannst in Ruhe weiterlesen.",
      );
    }
  };

  return (
    <div className="live-timeline">
      <div className="live-timeline__controls">
        <label htmlFor={orderId}>
          {en ? "Order" : "Reihenfolge"}
          <select
            id={orderId}
            value={order}
            onChange={(e) => setOrder(e.target.value as typeof order)}
            aria-controls={feedId}
          >
            <option value="newest">{t(locale, "latest")}</option>
            <option value="oldest">{t(locale, "oldest")}</option>
          </select>
        </label>
        {event.status !== "archived" && (
          <button type="button" onClick={toggleUpdates} aria-controls={feedId}>
            {frozen ? (
              <Play size={18} aria-hidden="true" />
            ) : (
              <Pause size={18} aria-hidden="true" />
            )}
            {frozen
              ? en
                ? "Resume updates"
                : "Aktualisierung fortsetzen"
              : en
                ? "Pause updates"
                : "Aktualisierung pausieren"}
          </button>
        )}
      </div>
      <p className="sr-only" role="status" aria-atomic="true">
        {announcement}
      </p>
      {frozen && (
        <p className="live-timeline__paused">
          {en ? "Updates are paused." : "Aktualisierung pausiert."}{" "}
          {pending > 0 &&
            (en
              ? `${pending} new ${pending === 1 ? "update" : "updates"} available.`
              : `${pending} ${pending === 1 ? "neuer Beitrag" : "neue Beiträge"} verfügbar.`)}
        </p>
      )}
      <div id={feedId}>
        <Timeline
          posts={orderPosts(frozen ?? posts, order)}
          locale={locale}
          relative={!frozen && event.showRelativeTime}
          timezone={event.timezone}
          compact={compact}
        />
      </div>
    </div>
  );
}
